import React from 'react';

/**
 * Toggle Component
 *
 * A reusable, accessible toggle switch component that follows the Stock Sentry
 * brand design (using purple accent color).
 *
 * Accessibility:
 * - Uses role="switch" for screen readers
 * - Supports aria-checked for toggle state
 * - Full keyboard navigation (Enter, Space)
 * - Focus visible styles for keyboard users
 *
 * @example
 * ```tsx
 * <Toggle
 *   enabled={isEnabled}
 *   onChange={(value) => setIsEnabled(value)}
 *   label="Enable feature"
 * />
 * ```
 */

export interface ToggleProps {
  /** Current state of the toggle */
  enabled: boolean;
  /** Callback when state changes */
  onChange: (enabled: boolean) => void;
  /** Accessibility label for screen readers */
  label: string;
  /** Whether the toggle is disabled */
  disabled?: boolean;
}

export function Toggle({ enabled, onChange, label, disabled = false }: ToggleProps) {
  return (
    <div
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onChange(!enabled)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          e.preventDefault();
          onChange(!enabled);
        }
      }}
      style={{
        width: '40px',
        height: '22px',
        borderRadius: '11px',
        backgroundColor: enabled ? '#7C3AED' : 'var(--p-color-bg-fill-disabled)',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.2s ease',
        opacity: disabled ? 0.6 : 1,
        flexShrink: 0,
        border: 'none',
        padding: 0,
        outline: 'none',
        boxShadow: 'none',
      }}
    >
      <div
        style={{
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          backgroundColor: 'white',
          position: 'absolute',
          top: '2px',
          left: enabled ? '20px' : '2px',
          transition: 'left 0.2s ease',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
          pointerEvents: 'none',
        }}
      />
      {/* Focus ring for keyboard users handled via CSS in a real app, 
          but for simplicity we'll keep it as a standard focus state or 
          rely on browser default since we use div/tabIndex */}
      <style>{`
        [role="switch"]:focus-visible {
          outline: 2px solid var(--p-color-border-focus) !important;
          outline-offset: 2px !important;
        }
      `}</style>
    </div>
  );
}
